// console/src/components/SkillManager.tsx
import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { API_URL, getAuthHeaders } from '../services/api';
import SkillCatalogBrowser from './SkillCatalog';

interface Skill {
	id: number;
	skill_slug: string;
	skill_name: string;
	github_url: string | null;
	source: string;
	install_status?: string;
	error_message?: string | null;
	added_at: string;
}

interface SkillManagerProps {
	instanceId?: string;   // If provided, manages instance (workspace) skills
	teamMode?: boolean;    // If true, manages team-level skills
	readOnly?: boolean;    // When showing team defaults on instance page
}

export default function SkillManager({ instanceId, teamMode, readOnly }: SkillManagerProps) {
	const { firebaseUser } = useAuth();
	const [skills, setSkills] = useState<Skill[]>([]);
	const [loading, setLoading] = useState(true);
	const [customEnabled, setCustomEnabled] = useState(false);
	const [, setSource] = useState<'team' | 'instance'>('team');
	const [showCatalog, setShowCatalog] = useState(false);
	const [manualSlug, setManualSlug] = useState('');
	const [adding, setAdding] = useState(false);
	const [removing, setRemoving] = useState<string | null>(null);
	const [syncing, setSyncing] = useState(false);
	const [error, setError] = useState<string | null>(null);

	const getHeaders = useCallback(async (): Promise<Record<string, string>> => {
		if (!firebaseUser) return { 'Content-Type': 'application/json' };
		const idToken = await firebaseUser.getIdToken();
		return getAuthHeaders(idToken);
	}, [firebaseUser]);

	const fetchSkills = useCallback(async () => {
		if (!firebaseUser) return;
		setError(null);
		try {
			const headers = await getHeaders();
			const url = instanceId
				? `${API_URL}/api/v1/instances/${instanceId}/skills`
				: `${API_URL}/api/v1/skills/team`;
			const resp = await fetch(url, { headers });
			if (!resp.ok) {
				const text = await resp.text();
				throw new Error(text || `HTTP ${resp.status}`);
			}
			const data = await resp.json();

			if (instanceId) {
				setCustomEnabled(data.custom_enabled ?? false);
				setSource(data.source ?? 'team');
			}
			setSkills(data.skills || []);
		} catch (err: any) {
			setError(err.message || 'Failed to load skills');
			setSkills([]);
		} finally {
			setLoading(false);
		}
	}, [getHeaders, instanceId, firebaseUser]);

	useEffect(() => {
		fetchSkills();
	}, [fetchSkills]);

	// Poll every 5 seconds while any skill is in a pending install state
	useEffect(() => {
		const hasPending = skills.some(s => s.install_status === 'pending');
		if (!hasPending || !instanceId) return;

		const interval = setInterval(() => {
			fetchSkills();
		}, 5000);

		return () => clearInterval(interval);
	}, [skills, instanceId, fetchSkills]);

	const handleToggleCustom = async (enabled: boolean) => {
		if (!instanceId) return;
		try {
			const headers = await getHeaders();
			await fetch(`${API_URL}/api/v1/instances/${instanceId}/skills/custom`, {
				method: 'PUT',
				headers,
				body: JSON.stringify({ enabled }),
			});
			setCustomEnabled(enabled);
			fetchSkills();
		} catch (err: any) {
			setError(err.message || 'Failed to update custom skills setting');
		}
	};

	const handleAddSkill = async (skill: {
		skill_slug: string;
		skill_name?: string;
		github_url?: string;
		source: 'catalog' | 'manual';
	}) => {
		setAdding(true);
		setError(null);
		try {
			const headers = await getHeaders();
			const url = instanceId
				? `${API_URL}/api/v1/instances/${instanceId}/skills`
				: `${API_URL}/api/v1/skills/team`;
			const resp = await fetch(url, {
				method: 'POST',
				headers,
				body: JSON.stringify(skill),
			});
			if (resp.ok) {
				fetchSkills();
			} else {
				const data = await resp.json().catch(() => ({}));
				setError(data.error || `Failed to add skill (HTTP ${resp.status})`);
			}
		} catch (err: any) {
			setError(err.message || 'Failed to add skill');
		} finally {
			setAdding(false);
			setManualSlug('');
		}
	};

	const handleRemoveSkill = async (slug: string) => {
		setRemoving(slug);
		setError(null);
		try {
			const headers = await getHeaders();
			const url = instanceId
				? `${API_URL}/api/v1/instances/${instanceId}/skills/${slug}`
				: `${API_URL}/api/v1/skills/team/${slug}`;
			const resp = await fetch(url, { method: 'DELETE', headers });
			if (resp.ok) {
				fetchSkills();
			} else {
				const data = await resp.json().catch(() => ({}));
				setError(data.error || `Failed to remove skill (HTTP ${resp.status})`);
			}
		} catch (err: any) {
			setError(err.message || 'Failed to remove skill');
		} finally {
			setRemoving(null);
		}
	};

	const handleManualAdd = () => {
		const trimmed = manualSlug.trim();
		if (!trimmed) return;
		const isUrl = trimmed.startsWith('http');
		handleAddSkill({
			skill_slug: isUrl ? (trimmed.split('/').pop() || trimmed) : trimmed,
			github_url: isUrl ? trimmed : undefined,
			source: 'manual',
		});
	};

	const handleSyncTeamSkills = async () => {
		if (!instanceId) return;
		setSyncing(true);
		setError(null);
		try {
			const headers = await getHeaders();
			const resp = await fetch(`${API_URL}/api/v1/instances/${instanceId}/skills/sync-team`, {
				method: 'POST',
				headers,
			});
			if (!resp.ok) {
				const data = await resp.json().catch(() => ({}));
				throw new Error(data.error || `Failed to sync team skills (HTTP ${resp.status})`);
			}
			const data = await resp.json();
			if (data.custom_enabled) {
				setCustomEnabled(true);
			}
			fetchSkills();
		} catch (err: any) {
			setError(err.message || 'Failed to sync team skills');
		} finally {
			setSyncing(false);
		}
	};

	const installedSlugs = new Set(skills.map(s => s.skill_slug));
	// Editable when: not readOnly AND (team mode is on OR custom list is enabled for instance)
	const isEditable = !readOnly && (teamMode || customEnabled);

	const statusColor = (status?: string) => {
		switch (status) {
			case 'installed':
				return 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300';
			case 'pending':
				return 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300';
			case 'failed':
				return 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300';
			default:
				return 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400';
		}
	};

	return (
		<div className="space-y-6">
			{/* Custom skills toggle — instance mode only, not teamMode */}
			{instanceId && !teamMode && (
				<div className="flex items-center justify-between p-4 bg-slate-50 dark:bg-slate-800/50 rounded-xl border border-slate-200 dark:border-slate-800">
					<div>
						<div className="font-medium text-sm text-slate-900 dark:text-white">
							Use custom skills
						</div>
						<div className="text-xs text-slate-500 dark:text-slate-400">
							{customEnabled
								? 'This workspace uses its own skill list'
								: 'Using team defaults — toggle to customize skills for this workspace'}
						</div>
					</div>
					<button
						onClick={() => handleToggleCustom(!customEnabled)}
						className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors cursor-pointer ${
							customEnabled
								? 'bg-primary-600'
								: 'bg-slate-300 dark:bg-slate-600'
						}`}
						aria-label={customEnabled ? 'Disable custom skills' : 'Enable custom skills'}
					>
						<span
							className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
								customEnabled ? 'translate-x-6' : 'translate-x-1'
							}`}
						/>
					</button>
				</div>
			)}

			{/* Source indicator when custom is off in instance mode */}
			{instanceId && !customEnabled && (
				<div className="px-4 py-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl text-sm text-blue-700 dark:text-blue-300">
					<div className="flex items-center justify-between gap-3">
						<span>Showing team default skills. Toggle &ldquo;Use custom skills&rdquo; to manage skills for this workspace independently.</span>
						{skills.length > 0 && (
							<button
								onClick={handleSyncTeamSkills}
								disabled={syncing}
								className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-xs font-medium disabled:opacity-50 cursor-pointer transition-colors whitespace-nowrap shrink-0"
							>
								{syncing ? 'Installing...' : 'Install to Workspace'}
							</button>
						)}
					</div>
				</div>
			)}

			{/* Error banner */}
			{error && (
				<div className="px-4 py-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl text-sm text-red-700 dark:text-red-300">
					{error}
				</div>
			)}

			{/* Skill list */}
			{loading ? (
				<div className="text-center py-8 text-slate-500 dark:text-slate-400">
					Loading skills...
				</div>
			) : skills.length === 0 ? (
				<div className="text-center py-8">
					<div className="text-slate-400 dark:text-slate-500 mb-2">No skills configured</div>
					{isEditable && (
						<div className="text-sm text-slate-500 dark:text-slate-400">
							Add skills from the catalog or enter a skill slug manually
						</div>
					)}
				</div>
			) : (
				<div className="space-y-2">
					{skills.map(skill => (
						<div
							key={skill.skill_slug}
							className="flex items-center justify-between p-4 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl"
						>
							<div className="flex-1 min-w-0">
								<div className="flex items-center gap-2 flex-wrap">
									<span className="font-medium text-sm text-slate-900 dark:text-white">
										{skill.skill_name || skill.skill_slug}
									</span>
									{skill.install_status && (
										<span
											className={`text-xs px-2 py-0.5 rounded-full font-medium ${statusColor(skill.install_status)}`}
										>
											{skill.install_status === 'pending'
												? 'Installing...'
												: skill.install_status}
										</span>
									)}
									{skill.source === 'manual' && (
										<span className="text-xs px-2 py-0.5 rounded-full bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400">
											Manual
										</span>
									)}
								</div>
								{skill.github_url && (
									<a
										href={skill.github_url}
										target="_blank"
										rel="noopener noreferrer"
										className="text-xs text-slate-400 hover:text-primary-500 truncate block mt-0.5"
									>
										{skill.github_url}
									</a>
								)}
								{skill.error_message && (
									<div className="text-xs text-red-500 mt-1">
										{skill.error_message}
									</div>
								)}
							</div>
							{isEditable && (
								<button
									onClick={() => handleRemoveSkill(skill.skill_slug)}
									disabled={removing === skill.skill_slug}
									className="ml-4 p-2 text-slate-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg cursor-pointer disabled:opacity-50 transition-colors"
									aria-label={`Remove ${skill.skill_name || skill.skill_slug}`}
								>
									{removing === skill.skill_slug ? (
										<svg
											className="w-4 h-4 animate-spin"
											fill="none"
											viewBox="0 0 24 24"
										>
											<circle
												className="opacity-25"
												cx="12"
												cy="12"
												r="10"
												stroke="currentColor"
												strokeWidth="4"
											/>
											<path
												className="opacity-75"
												fill="currentColor"
												d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
											/>
										</svg>
									) : (
										<svg
											className="w-4 h-4"
											fill="none"
											stroke="currentColor"
											viewBox="0 0 24 24"
										>
											<path
												strokeLinecap="round"
												strokeLinejoin="round"
												strokeWidth={2}
												d="M6 18L18 6M6 6l12 12"
											/>
										</svg>
									)}
								</button>
							)}
						</div>
					))}
				</div>
			)}

			{/* Add actions — only shown when editable */}
			{isEditable && (
				<div className="space-y-3">
					<div className="flex gap-2">
						<input
							type="text"
							placeholder="Enter skill slug or GitHub URL..."
							value={manualSlug}
							onChange={e => setManualSlug(e.target.value)}
							onKeyDown={e => e.key === 'Enter' && handleManualAdd()}
							className="flex-1 px-4 py-2 rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-white placeholder-slate-400 text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none"
						/>
						<button
							onClick={handleManualAdd}
							disabled={!manualSlug.trim() || adding}
							className="px-4 py-2 bg-slate-900 dark:bg-white text-white dark:text-slate-900 rounded-lg text-sm font-medium hover:bg-slate-800 dark:hover:bg-slate-100 disabled:opacity-50 cursor-pointer transition-colors"
						>
							{adding ? 'Adding...' : 'Add'}
						</button>
					</div>
					<button
						onClick={() => setShowCatalog(true)}
						className="w-full px-4 py-3 border-2 border-dashed border-slate-300 dark:border-slate-700 rounded-xl text-sm font-medium text-slate-500 dark:text-slate-400 hover:border-primary-400 hover:text-primary-500 cursor-pointer transition-colors"
					>
						Browse Skill Catalog
					</button>
				</div>
			)}

			{/* Catalog modal */}
			{showCatalog && (
				<SkillCatalogBrowser
					onAddSkill={skill => {
						handleAddSkill(skill);
					}}
					installedSlugs={installedSlugs}
					onClose={() => setShowCatalog(false)}
				/>
			)}
		</div>
	);
}
